// =====================================================================================
// INVRT Radar - web console - the operator Organisations section (Story PS5)
//
// The operator's organisation surface on the PS2 seam (contracts C1 and C2): the list is
// the SHARED E04A organisations read (the same cache entry the console header and the
// access section use, so the console can never disagree about which organisations exist),
// creation is C1 (name, the live tenant/reseller kind, an optional reseller parent, and a
// recorded reason), and invitation is C2 (email, role, and the modules and campaigns the
// invitee receives, chosen from the catalogue reads the console already has).
//
// Authority stays on the server: this section renders states, it never decides them. The
// server verifies the parent's kind, the role vocabulary and every module code and
// campaign id; the section's refusals (a missing reason, an empty name) are the courtesy
// copy so a bad request never leaves the browser. Where the seam cannot answer a question
// (the E04A list rows carry no org_kind), the section labels only what it has VERIFIED
// through this console's own C1 responses, and says nothing about the rest.
// =====================================================================================

import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { EmptyState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Chip, type ChipSignal } from "@/components/ui/chip";
import { DataTable, type DataTableRow } from "@/components/ui/data-table";
import { Dialog } from "@/components/ui/dialog";
import { Input, Select } from "@/components/ui/input";
import { MobileRows, type MobileRow } from "@/components/ui/mobile-rows";
import { useToast } from "@/components/ui/toast";
import { useApiClient } from "@/hooks/useApi";
import { isApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
  createOrganisation,
  inviteOrganisationMember,
  parseCreatedOrganisation,
  type InviteMemberResponse,
  type MembershipRole,
  type OrganisationKind,
} from "@/lib/organisationsAdmin";
import { readCatalogueKind } from "@/pages/operator/catalogueReader";
import { hiddenTestRecordsLine, isTestOrganisation, TEST_RECORDS_TOGGLE_LABEL } from "@/pages/operator/testRecords";
import { useOperatorOrganisations } from "@/pages/operator/useOperatorOrganisations";
import type { AdminOrganisation } from "@/lib/types.entitlements";

type DialogState =
  | { action: "create" }
  | { action: "invite"; org: AdminOrganisation }
  // Story R6: the owner's lifecycle actions. Close is the soft off-switch; Re-open is its
  // inverse. Both exist on every row by status; the server owns every rule (a reseller
  // organisation refuses the close with organisation_reseller_immutable).
  | { action: "rename"; org: AdminOrganisation }
  | { action: "close"; org: AdminOrganisation }
  | { action: "reopen"; org: AdminOrganisation };

interface CreateForm {
  name: string;
  kind: OrganisationKind;
  parentId: string;
  reason: string;
}

interface InviteForm {
  email: string;
  role: MembershipRole;
  moduleCodes: string[];
  campaignIds: string[];
  reason: string;
}

interface RenameForm {
  name: string;
  reason: string;
}

interface StatusForm {
  reason: string;
}

interface FormError {
  /** The refusal, or the server's own copy, surfaced verbatim. */
  message: string;
  /** The honest consequence line, rendered beside it. Empty for a local refusal. */
  consequence: string;
}

/** The C3 PATCH body: the reason is required; name and status are the optional changes. */
interface UpdateOrganisationBody {
  name?: string;
  status?: "active" | "closed";
  reason: string;
}

/**
 * C3 (story R6): rename an organisation and/or flip its soft status, always with the
 * recorded reason. Page-private on purpose: the story's allowed surface is this file, and
 * the 200 body is the same full audited row C1 returns, so the lib's existing fail-closed
 * parser stands guard unchanged.
 */
async function updateOrganisation(
  api: ReturnType<typeof useApiClient>,
  orgId: string,
  input: UpdateOrganisationBody,
) {
  const body = {
    ...(input.name === undefined ? {} : { name: input.name }),
    ...(input.status === undefined ? {} : { status: input.status }),
    reason: input.reason,
  };
  const response = await api.patch<unknown>(
    `/admin/organisations/${encodeURIComponent(orgId)}`,
    body,
    { requiresOrg: false, includeOrg: false },
  );
  return parseCreatedOrganisation(response);
}

function statusSignal(status: AdminOrganisation["status"]): ChipSignal | undefined {
  if (status === "active") return "buy";
  if (status === "suspended") return "risk";
  return undefined;
}

function localRefusal(message: string, consequence: string): FormError {
  return { message: `${message} ${consequence}`, consequence: "" };
}

/** The server's copy is surfaced verbatim; only the consequence line is ours. */
function serverErrorCopy(error: unknown, consequence: string): FormError {
  if (isApiError(error) && error.httpStatus === 403) {
    return { message: "Your current access does not permit this action.", consequence };
  }
  return {
    message: "The outcome could not be confirmed. Your draft has been kept.",
    consequence: "Review the organisation and invitation status before trying again.",
  };
}

/** The honest consequence line for a refused R6 mutation, named by the open dialog. */
function updateConsequence(dialog: DialogState | null): string {
  if (dialog?.action === "rename") return "The organisation was not renamed.";
  if (dialog?.action === "close") return "The organisation was not closed.";
  if (dialog?.action === "reopen") return "The organisation was not re-opened.";
  return "";
}

function createFormError(values: CreateForm): FormError | null {
  const reason = values.reason.trim();
  if (reason.length < 3 || reason.length > 500) {
    return localRefusal("Provide a reason between 3 and 500 characters.", "No organisation was created.");
  }
  const name = values.name.trim();
  if (name.length < 1 || name.length > 120) {
    return localRefusal("Provide a name of 1 to 120 characters.", "No organisation was created.");
  }
  if (values.kind === "reseller" && values.parentId) {
    return localRefusal("A reseller organisation cannot be created under a parent.", "No organisation was created.");
  }
  return null;
}

function inviteFormError(values: InviteForm): FormError | null {
  const reason = values.reason.trim();
  if (reason.length < 3 || reason.length > 500) {
    return localRefusal("Provide a reason between 3 and 500 characters.", "No invitation was sent.");
  }
  const email = values.email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return localRefusal("Provide a valid email address.", "No invitation was sent.");
  }
  return null;
}

/** The C3 contract pins the reason at 1-500 characters (tighter than create/invite's 3-500). */
function renameFormError(values: RenameForm): FormError | null {
  const reason = values.reason.trim();
  if (reason.length < 1 || reason.length > 500) {
    return localRefusal("Provide a reason between 1 and 500 characters.", "The organisation was not renamed.");
  }
  const name = values.name.trim();
  if (name.length < 1 || name.length > 120) {
    return localRefusal("Provide a name of 1 to 120 characters.", "The organisation was not renamed.");
  }
  return null;
}

function statusFormError(values: StatusForm, consequence: string): FormError | null {
  const reason = values.reason.trim();
  if (reason.length < 1 || reason.length > 500) {
    return localRefusal("Provide a reason between 1 and 500 characters.", consequence);
  }
  return null;
}

export function OrganisationsSection({ filter }: { filter: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { push } = useToast();
  // The dossier (R5) is its own route; the action only navigates to it.
  const navigate = useNavigate();
  // The shared E04A read: the same cache entry the console header and the access section
  // use, so the organisations list costs the wire exactly one read however many surfaces
  // show it.
  const organisations = useOperatorOrganisations();
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [createForm, setCreateForm] = useState<CreateForm>({ name: "", kind: "tenant", parentId: "", reason: "" });
  const [inviteForm, setInviteForm] = useState<InviteForm>({ email: "", role: "member", moduleCodes: [], campaignIds: [], reason: "" });
  const [renameForm, setRenameForm] = useState<RenameForm>({ name: "", reason: "" });
  const [statusForm, setStatusForm] = useState<StatusForm>({ reason: "" });
  const [error, setError] = useState<FormError | null>(null);
  // Probe/test organisations stay behind the explicit toggle, default OFF (story P2-UI,
  // Mark's ruling). They are fixtures of the live-fire estate, not tenants.
  const [showTestRecords, setShowTestRecords] = useState(false);
  // Organisation kinds VERIFIED through this console's own C1 responses. The E04A list
  // rows carry no org_kind, so nothing is claimed about an organisation the console did
  // not create in this session.
  const [knownKinds, setKnownKinds] = useState<Record<string, OrganisationKind>>({});
  // The invitations confirmed in this session. There is no invitation-history read in the
  // seam, so the panel says exactly that instead of inventing a history.
  const [invitations, setInvitations] = useState<Array<InviteMemberResponse & { orgName: string }>>([]);
  const dialogInvoker = useRef<HTMLElement | null>(null);

  const allOrganisations = useMemo(() => organisations.data ?? [], [organisations.data]);
  const testRecordCount = useMemo(
    () => allOrganisations.filter((organisation) => isTestOrganisation(organisation.name)).length,
    [allOrganisations],
  );
  const filtered = useMemo(
    () =>
      allOrganisations
        .filter((organisation) => showTestRecords || !isTestOrganisation(organisation.name))
        .filter((organisation) =>
          organisation.name.toLocaleLowerCase().includes(filter),
        ),
    [allOrganisations, filter, showTestRecords],
  );

  // The invite dialog's choosers are fed by the catalogue reads the console already has;
  // they need complete option sets, so the walk happens while the dialog is open.
  const inviteOptions = useQuery({
    queryKey: ["operator", "organisations", "invite-options"],
    enabled: dialog?.action === "invite",
    queryFn: async () => {
      const [modules, campaigns] = await Promise.all([
        readCatalogueKind(api, "module"),
        readCatalogueKind(api, "campaign"),
      ]);
      return {
        modules: modules.filter((item) => item.status === "active"),
        campaigns: campaigns.filter((item) => item.status === "active"),
      };
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: CreateForm) =>
      await createOrganisation(api, {
        name: values.name.trim(),
        org_kind: values.kind,
        parent_org_id: values.kind === "tenant" && values.parentId ? values.parentId : null,
        reason: values.reason.trim(),
      }),
    onSuccess: (response) => {
      push({ tone: "success", title: "Organisation created", body: `${response.organisation.name} now exists and is ${response.organisation.status}.` });
      setKnownKinds((current) => ({ ...current, [response.organisation.id]: response.organisation.org_kind }));
      setDialog(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["operator", "access", "organisations"] });
    },
    onError: (cause) => setError(serverErrorCopy(cause, "No organisation was created.")),
  });

  const inviteMutation = useMutation({
    mutationFn: async ({ org, values }: { org: AdminOrganisation; values: InviteForm }) =>
      await inviteOrganisationMember(api, org.id, {
        email: values.email.trim(),
        membership_role: values.role,
        module_codes: values.moduleCodes,
        campaign_ids: values.campaignIds,
        reason: values.reason.trim(),
      }),
    onSuccess: (response, variables) => {
      push({
        tone: "success",
        title: "Invitation sent",
        body: `${response.email} was invited to join ${variables.org.name}.`,
      });
      for (const entry of response.campaign_anchors ?? []) {
        if (entry.anchor !== null && entry.anchor.anchored) continue;
        push({
          tone: "warning",
          title: "Campaign not yet linked",
          body: `The invitation stands, but campaign ${entry.campaign_id} was not linked to its delivery board. Use Re-link on the Access screen.`,
        });
      }
      setInvitations((current) => [...current, { ...response, orgName: variables.org.name }]);
      setDialog(null);
      setError(null);
    },
    onError: (cause) => setError(serverErrorCopy(cause, "No invitation was sent.")),
  });

  // Story R6: one mutation serves rename, close and re-open; the server audits every one.
  const updateMutation = useMutation({
    mutationFn: async ({ org, body }: { org: AdminOrganisation; body: UpdateOrganisationBody }) =>
      await updateOrganisation(api, org.id, body),
    onSuccess: (response) => {
      push({
        tone: "success",
        title: "Organisation updated",
        body: `${response.organisation.name} is now ${response.organisation.status}. Nothing was deleted.`,
      });
      setDialog(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["operator", "access", "organisations"] });
    },
    onError: (cause) =>
      setError(serverErrorCopy(cause, updateConsequence(dialog))),
  });

  useEffect(() => {
    if (
      dialog !== null ||
      createMutation.isPending ||
      inviteMutation.isPending ||
      updateMutation.isPending
    ) return;
    const invoker = dialogInvoker.current;
    dialogInvoker.current = null;
    if (invoker?.isConnected) invoker.focus();
  }, [dialog, createMutation.isPending, inviteMutation.isPending, updateMutation.isPending]);

  const openDialog = (state: DialogState, invoker: HTMLElement) => {
    if (createMutation.isPending || inviteMutation.isPending || updateMutation.isPending) return;
    dialogInvoker.current = invoker;
    setDialog(state);
    setError(null);
    setCreateForm({ name: "", kind: "tenant", parentId: "", reason: "" });
    setInviteForm({ email: "", role: "member", moduleCodes: [], campaignIds: [], reason: "" });
    setRenameForm({ name: "", reason: "" });
    setStatusForm({ reason: "" });
  };

  const submitCreate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (createMutation.isPending) return;
    const problem = createFormError(createForm);
    if (problem) {
      setError(problem);
      return;
    }
    createMutation.mutate(createForm);
  };

  const submitInvite = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!dialog || dialog.action !== "invite" || inviteMutation.isPending) return;
    const problem = inviteFormError(inviteForm);
    if (problem) {
      setError(problem);
      return;
    }
    inviteMutation.mutate({ org: dialog.org, values: inviteForm });
  };

  const submitRename = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!dialog || dialog.action !== "rename" || updateMutation.isPending) return;
    const problem = renameFormError(renameForm);
    if (problem) {
      setError(problem);
      return;
    }
    updateMutation.mutate({
      org: dialog.org,
      body: { name: renameForm.name.trim(), reason: renameForm.reason.trim() },
    });
  };

  const submitStatus = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!dialog || (dialog.action !== "close" && dialog.action !== "reopen")) return;
    if (updateMutation.isPending) return;
    const status = dialog.action === "close" ? "closed" : "active";
    const problem = statusFormError(statusForm, updateConsequence(dialog));
    if (problem) {
      setError(problem);
      return;
    }
    updateMutation.mutate({ org: dialog.org, body: { status, reason: statusForm.reason.trim() } });
  };

  const updateCreate = (key: keyof CreateForm) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setCreateForm((current) => ({ ...current, [key]: event.target.value }));
  const updateInvite = (key: keyof InviteForm) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setInviteForm((current) => ({ ...current, [key]: event.target.value }));

  if (organisations.isLoading) return <LoadingState label="Loading the organisation list…" />;
  if (organisations.isError) {
    if (isApiError(organisations.error) && organisations.error.httpStatus === 403) {
      return (
        <EmptyState
          title="You do not have permission to view organisations."
          body="The server refused this operator request."
        />
      );
    }
    // The shared read failing is already announced once on this console by the access
    // section's full ErrorState (token copy and correlation id). This section carries its
    // own distinctly worded error tone rather than a second identical panel, and retries
    // the same read.
    return (
      <EmptyState
        title="The organisation list could not be loaded."
        body="The server response did not match the reviewed contract. The access section carries the full error detail and correlation id."
        tone="error"
        action={
          <Button type="button" size="sm" variant="secondary" className="h-11" onClick={() => void organisations.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  const anyPending = createMutation.isPending || inviteMutation.isPending || updateMutation.isPending;

  const desktopRows = (rows: AdminOrganisation[]): DataTableRow[] =>
    rows.map((organisation) => ({
      id: organisation.id,
      organisation: (
        <div className="flex flex-col">
          <span className="font-medium">{organisation.name}</span>
          {knownKinds[organisation.id] ? (
            <span className="text-xs text-ink-muted">
              {knownKinds[organisation.id] === "reseller" ? "Reseller" : "Tenant"} (created in this session)
            </span>
          ) : null}
        </div>
      ),
      status: (
        <Chip signal={statusSignal(organisation.status)} dot={organisation.status === "active"}>
          {organisation.status}
        </Chip>
      ),
      created: <span className="text-xs text-ink-muted">{formatDateTime(organisation.created_at)}</span>,
      actions: (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 min-w-11"
            disabled={anyPending}
            onClick={() => navigate(`/operator/organisations/${organisation.id}`)}
            aria-label={`View the dossier for ${organisation.name}`}
          >
            Dossier
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 min-w-11"
            disabled={anyPending}
            onClick={(event) => openDialog({ action: "rename", org: organisation }, event.currentTarget)}
            aria-label={`Rename ${organisation.name}`}
          >
            Rename
          </Button>
          {organisation.status === "closed" ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-11 min-w-11"
              disabled={anyPending}
              onClick={(event) => openDialog({ action: "reopen", org: organisation }, event.currentTarget)}
              aria-label={`Re-open ${organisation.name}`}
            >
              Re-open
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-11 min-w-11"
              disabled={anyPending}
              onClick={(event) => openDialog({ action: "close", org: organisation }, event.currentTarget)}
              aria-label={`Close ${organisation.name}`}
            >
              Close
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 min-w-11"
            disabled={organisation.status !== "active" || anyPending}
            onClick={(event) => openDialog({ action: "invite", org: organisation }, event.currentTarget)}
            aria-label={`Invite a user into ${organisation.name}`}
          >
            Invite user
          </Button>
        </div>
      ),
    }));

  const mobileRows = (rows: AdminOrganisation[]): MobileRow[] =>
    rows.map((organisation) => ({
      id: organisation.id,
      name: organisation.name,
      meta: `Created ${formatDateTime(organisation.created_at)}`,
      signal: statusSignal(organisation.status),
      signalText: organisation.status,
      actions: (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 min-w-11"
            disabled={anyPending}
            onClick={() => navigate(`/operator/organisations/${organisation.id}`)}
            aria-label={`View the dossier for ${organisation.name} in mobile view`}
          >
            Dossier
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 min-w-11"
            disabled={anyPending}
            onClick={(event) => openDialog({ action: "rename", org: organisation }, event.currentTarget)}
            aria-label={`Rename ${organisation.name} in mobile view`}
          >
            Rename
          </Button>
          {organisation.status === "closed" ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-11 min-w-11"
              disabled={anyPending}
              onClick={(event) => openDialog({ action: "reopen", org: organisation }, event.currentTarget)}
              aria-label={`Re-open ${organisation.name} in mobile view`}
            >
              Re-open
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-11 min-w-11"
              disabled={anyPending}
              onClick={(event) => openDialog({ action: "close", org: organisation }, event.currentTarget)}
              aria-label={`Close ${organisation.name} in mobile view`}
            >
              Close
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 min-w-11"
            disabled={organisation.status !== "active" || anyPending}
            onClick={(event) => openDialog({ action: "invite", org: organisation }, event.currentTarget)}
            aria-label={`Invite a user into ${organisation.name} in mobile view`}
          >
            Invite user
          </Button>
        </div>
      ),
    }));

  let body: ReactNode;
  if (filtered.length === 0) {
    body = allOrganisations.length === 0
      ? <EmptyState title="No organisations yet" body="Create the first organisation to get started." />
      : <EmptyState title="No organisations match this filter." body="Change the filter above this list." tone="filtered" />;
  } else {
    body = (
      <>
        <div className="hidden md:block">
          <DataTable
            columns={[
              { key: "organisation", label: "Organisation" },
              { key: "status", label: "Status" },
              { key: "created", label: "Created" },
              { key: "actions", label: "Actions" },
            ]}
            rows={desktopRows(filtered)}
            label="Organisations desktop view"
            density="compact"
          />
        </div>
        <MobileRows label="Organisations mobile view" className="md:hidden" rows={mobileRows(filtered)} />
      </>
    );
  }

  return (
    /* The screen's one "Organisations" heading is the ScreenFrame h1 (story P2-UI); the
       section keeps the region name for heading/landmark navigation without repeating
       the title (the U-2 walk pinned the single-header law). */
    <section aria-label="Organisations" className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Button
          type="button"
          size="lg"
          disabled={anyPending}
          onClick={(event) => openDialog({ action: "create" }, event.currentTarget)}
        >
          Create organisation
        </Button>
      </div>
      {/* The probe/test-records toggle (story P2-UI). Offered only when the estate
          actually carries test organisations; default OFF either way. */}
      {testRecordCount > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
            <input
              type="checkbox"
              checked={showTestRecords}
              onChange={(event) => setShowTestRecords(event.target.checked)}
              className="h-5 w-5 rounded border-field-border text-primary accent-primary"
            />
            {TEST_RECORDS_TOGGLE_LABEL}
          </label>
          {!showTestRecords ? (
            <p className="text-sm text-ink-muted">{hiddenTestRecordsLine(testRecordCount)}</p>
          ) : null}
        </div>
      ) : null}
      {body}
      {invitations.length > 0 ? (
        <section aria-labelledby="invitations-heading" className="flex flex-col gap-3">
          <div>
            <h3 id="invitations-heading" className="text-base font-semibold text-ink">Invitations sent this session</h3>
            <p className="mt-1 text-sm text-ink-muted">
              This console keeps no invitation history; the audit trail is on the server.
            </p>
          </div>
          <div className="hidden md:block">
            <DataTable
              columns={[
                { key: "invitee", label: "Invitee" },
                { key: "organisation", label: "Organisation" },
                { key: "role", label: "Role" },
                { key: "access", label: "Receives" },
              ]}
              rows={invitations.map((invitation) => ({
                // One person can be invited into two organisations in one session; the
                // row identity is the pair, never the user alone.
                id: `${invitation.organisation_id}:${invitation.email}`,
                invitee: (
                  <div className="flex flex-col">
                    <span className="font-medium">{invitation.email}</span>
                    <span className="text-xs text-ink-muted">{invitation.invited ? "Invitation emailed" : "Joined from an existing account"}</span>
                  </div>
                ),
                organisation: invitation.orgName,
                role: <Chip signal={invitation.is_org_admin ? "comp" : "ctx"}>{invitation.is_org_admin ? "admin" : "member"}</Chip>,
                access: (
                  <span className="text-xs text-ink-muted">
                    {invitation.module_grants.map((grant) => grant.module_code).join(", ") || "No module grants"}
                  </span>
                ),
              }))}
              label="Invitations sent this session desktop view"
              density="compact"
            />
          </div>
          <MobileRows
            label="Invitations mobile view"
            className="md:hidden"
            rows={invitations.map((invitation) => ({
              // The same (organisation, person) pair as the desktop mirror.
              id: `${invitation.organisation_id}:${invitation.email}`,
              name: invitation.email,
              meta: `${invitation.orgName} · ${invitation.module_grants.map((grant) => grant.module_code).join(", ") || "No module grants"}`,
              signal: invitation.is_org_admin ? "comp" : "ctx",
              signalText: invitation.is_org_admin ? "admin" : "member",
            }))}
          />
        </section>
      ) : null}
      {dialog?.action === "create" ? (
        <CreateOrganisationDialog
          form={createForm}
          organisations={organisations.data ?? []}
          knownKinds={knownKinds}
          pending={createMutation.isPending}
          error={error}
          onChange={updateCreate}
          onSubmit={submitCreate}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.action === "invite" ? (
        <InviteOrganisationDialog
          org={dialog.org}
          form={inviteForm}
          options={inviteOptions.data ?? null}
          optionsPending={inviteOptions.isPending}
          pending={inviteMutation.isPending}
          error={error}
          onChange={updateInvite}
          onToggleModule={(code, checked) =>
            setInviteForm((current) => ({
              ...current,
              moduleCodes: checked ? [...current.moduleCodes, code] : current.moduleCodes.filter((value) => value !== code),
            }))
          }
          onToggleCampaign={(id, checked) =>
            setInviteForm((current) => ({
              ...current,
              campaignIds: checked ? [...current.campaignIds, id] : current.campaignIds.filter((value) => value !== id),
            }))
          }
          onSubmit={submitInvite}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.action === "rename" ? (
        <RenameOrganisationDialog
          org={dialog.org}
          form={renameForm}
          pending={updateMutation.isPending}
          error={error}
          onChange={(key) => (event) =>
            setRenameForm((current) => ({ ...current, [key]: event.target.value }))}
          onSubmit={submitRename}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.action === "close" || dialog?.action === "reopen" ? (
        <StatusOrganisationDialog
          org={dialog.org}
          closing={dialog.action === "close"}
          form={statusForm}
          pending={updateMutation.isPending}
          error={error}
          onChange={(event) => setStatusForm({ reason: event.target.value })}
          onSubmit={submitStatus}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}

function CreateOrganisationDialog({
  form,
  organisations,
  knownKinds,
  pending,
  error,
  onChange,
  onSubmit,
  onClose,
}: {
  form: CreateForm;
  organisations: AdminOrganisation[];
  knownKinds: Record<string, OrganisationKind>;
  pending: boolean;
  error: FormError | null;
  onChange: (key: keyof CreateForm) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  const isReseller = form.kind === "reseller";
  return (
    <Dialog
      open
      onClose={onClose}
      closeDisabled={pending}
      busy={pending}
      title="Create an organisation"
      description="The server checks the name, the parent and your authority. The reason is recorded in the audit trail, so history shows who created this organisation and why."
      footer={
        <>
          <Button type="button" variant="secondary" className="h-11" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" form="organisation-create-form" className="h-11" loading={pending}>
            Create organisation
          </Button>
        </>
      }
    >
      <form id="organisation-create-form" className="flex flex-col gap-4" onSubmit={onSubmit}>
        {pending ? (
          <p role="status" aria-live="polite" className="text-sm text-ink-muted">Creating the organisation. Please wait.</p>
        ) : null}
        {error?.message ? <p role="alert" className="text-sm text-danger">{error.message}</p> : null}
        {error?.consequence ? <p className="text-sm text-ink-muted">{error.consequence}</p> : null}
        <Input
          label="Name"
          value={form.name}
          onChange={onChange("name")}
          className="h-11"
          disabled={pending}
          maxLength={120}
          hint="The human name of the organisation, 1 to 120 characters."
        />
        <Select
          label="Kind"
          value={form.kind}
          onChange={(event) => onChange("kind")(event)}
          className="h-11"
          disabled={pending}
          hint="A reseller can have tenant organisations beneath it. A tenant sits under a reseller or stands alone."
        >
          <option value="tenant">Tenant</option>
          <option value="reseller">Reseller</option>
        </Select>
        {isReseller ? (
          <p className="text-sm text-ink-muted">A reseller organisation cannot be created under a parent.</p>
        ) : (
          <Select
            label="Parent organisation"
            value={form.parentId}
            onChange={(event) => onChange("parentId")(event)}
            className="h-11"
            disabled={pending}
            hint="Optional. Only reseller organisations can be parents; the server verifies the parent's kind and refuses anything else."
          >
            <option value="">No parent (top-level tenant)</option>
            {/* A closed organisation is never offered as a parent (story R6: it has left
                every selector); the management list itself keeps the row for Re-open. */}
            {organisations
              .filter((organisation) => organisation.status !== "closed")
              .map((organisation) => {
                const kind = knownKinds[organisation.id];
                const knownTenant = kind !== undefined && kind !== "reseller";
                const label = kind === "reseller"
                  ? `${organisation.name} (reseller)`
                  : knownTenant
                    ? `${organisation.name} (tenant, not a parent)`
                    : organisation.name;
                return (
                  <option key={organisation.id} value={organisation.id} disabled={knownTenant}>
                    {label}
                  </option>
                );
              })}
          </Select>
        )}
        <Input
          label="Reason"
          value={form.reason}
          onChange={onChange("reason")}
          className="h-11"
          disabled={pending}
          minLength={3}
          maxLength={500}
          hint="Recorded in the audit trail with your name, so the history explains this organisation."
        />
      </form>
    </Dialog>
  );
}

function InviteOrganisationDialog({
  org,
  form,
  options,
  optionsPending,
  pending,
  error,
  onChange,
  onToggleModule,
  onToggleCampaign,
  onSubmit,
  onClose,
}: {
  org: AdminOrganisation;
  form: InviteForm;
  options: { modules: Awaited<ReturnType<typeof readCatalogueKind>>; campaigns: Awaited<ReturnType<typeof readCatalogueKind>> } | null;
  optionsPending: boolean;
  pending: boolean;
  error: FormError | null;
  onChange: (key: keyof InviteForm) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onToggleModule: (code: string, checked: boolean) => void;
  onToggleCampaign: (id: string, checked: boolean) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  const modules = options?.modules ?? [];
  const campaigns = options?.campaigns ?? [];
  return (
    <Dialog
      open
      onClose={onClose}
      closeDisabled={pending}
      busy={pending}
      title={`Invite a user to ${org.name}`}
      description={`The invitee receives an email invitation to join ${org.name}. When they accept, they hold the role below plus the selected modules and campaigns; the reason is recorded in the audit trail.`}
      footer={
        <>
          <Button type="button" variant="secondary" className="h-11" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" form="organisation-invite-form" className="h-11" loading={pending}>
            Send invitation
          </Button>
        </>
      }
    >
      <form id="organisation-invite-form" className="flex flex-col gap-4" onSubmit={onSubmit}>
        {pending ? (
          <p role="status" aria-live="polite" className="text-sm text-ink-muted">Sending the invitation. Please wait.</p>
        ) : null}
        {error?.message ? <p role="alert" className="text-sm text-danger">{error.message}</p> : null}
        {error?.consequence ? <p className="text-sm text-ink-muted">{error.consequence}</p> : null}
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={onChange("email")}
          className="h-11"
          disabled={pending}
          hint="A person already in RADAR joins silently; anyone new receives the invitation email first."
        />
        <Select
          label="Role"
          value={form.role}
          onChange={(event) => onChange("role")(event)}
          className="h-11"
          disabled={pending}
          hint="Today's membership roles: admin can manage the organisation, member works within it."
        >
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </Select>
        <fieldset className="flex flex-col gap-2" disabled={pending}>
          <legend className="text-sm font-medium text-ink">Modules</legend>
          <p className="text-xs text-ink-muted">Chosen from the live catalogue; the server verifies every code.</p>
          {optionsPending ? <p role="status" className="text-xs text-ink-muted">Loading catalogue options…</p> : null}
          {modules.map((item) => (
            <label key={item.id} className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.moduleCodes.includes(item.code)}
                onChange={(event) => onToggleModule(item.code, event.target.checked)}
                disabled={pending}
              />
              {item.name} ({item.code})
            </label>
          ))}
          {!optionsPending && modules.length === 0 ? (
            <p className="text-xs text-ink-muted">No active modules in the catalogue.</p>
          ) : null}
        </fieldset>
        <fieldset className="flex flex-col gap-2" disabled={pending}>
          <legend className="text-sm font-medium text-ink">Campaigns</legend>
          <p className="text-xs text-ink-muted">The campaigns this person may work with; the server verifies every one.</p>
          {campaigns.map((item) => (
            <label key={item.id} className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.campaignIds.includes(item.id)}
                onChange={(event) => onToggleCampaign(item.id, event.target.checked)}
                disabled={pending}
              />
              {item.name} ({item.code})
            </label>
          ))}
          {campaigns.length === 0 && !optionsPending ? (
            <p className="text-xs text-ink-muted">No active campaigns in the catalogue.</p>
          ) : null}
        </fieldset>
        <Input
          label="Reason"
          value={form.reason}
          onChange={onChange("reason")}
          className="h-11"
          disabled={pending}
          minLength={3}
          maxLength={500}
          hint="Recorded in the audit trail with your name, so the history explains this invitation."
        />
      </form>
    </Dialog>
  );
}

function RenameOrganisationDialog({
  org,
  form,
  pending,
  error,
  onChange,
  onSubmit,
  onClose,
}: {
  org: AdminOrganisation;
  form: RenameForm;
  pending: boolean;
  error: FormError | null;
  onChange: (key: keyof RenameForm) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open
      onClose={onClose}
      closeDisabled={pending}
      busy={pending}
      title={`Rename ${org.name}`}
      description="The server checks the name and your authority. The reason is recorded in the audit trail with your name, so the history explains the rename."
      footer={
        <>
          <Button type="button" variant="secondary" className="h-11" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" form="organisation-rename-form" className="h-11" loading={pending}>
            Save name
          </Button>
        </>
      }
    >
      <form id="organisation-rename-form" className="flex flex-col gap-4" onSubmit={onSubmit}>
        {pending ? (
          <p role="status" aria-live="polite" className="text-sm text-ink-muted">Saving the name. Please wait.</p>
        ) : null}
        {error?.message ? <p role="alert" className="text-sm text-danger">{error.message}</p> : null}
        {error?.consequence ? <p className="text-sm text-ink-muted">{error.consequence}</p> : null}
        <Input
          label="Name"
          value={form.name}
          onChange={onChange("name")}
          className="h-11"
          disabled={pending}
          maxLength={120}
          hint="The human name of the organisation, 1 to 120 characters."
        />
        <Input
          label="Reason"
          value={form.reason}
          onChange={onChange("reason")}
          className="h-11"
          disabled={pending}
          maxLength={500}
          hint="Required. Recorded in the audit trail with your name."
        />
      </form>
    </Dialog>
  );
}

function StatusOrganisationDialog({
  org,
  closing,
  form,
  pending,
  error,
  onChange,
  onSubmit,
  onClose,
}: {
  org: AdminOrganisation;
  closing: boolean;
  form: StatusForm;
  pending: boolean;
  error: FormError | null;
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  const title = closing ? `Close ${org.name}` : `Re-open ${org.name}`;
  const submitLabel = closing ? "Close organisation" : "Re-open organisation";
  return (
    <Dialog
      open
      onClose={onClose}
      closeDisabled={pending}
      busy={pending}
      title={title}
      description={closing
        ? `Closing ${org.name} switches it off everywhere it is used while keeping every record. Nothing is deleted, and the organisation can be re-opened later with a reason. The reason you give is recorded in the audit trail with your name.`
        : `Re-opening ${org.name} puts it back to work. Nothing was deleted while it was closed. The reason you give is recorded in the audit trail with your name.`}
      footer={
        <>
          <Button type="button" variant="secondary" className="h-11" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" form="organisation-status-form" className="h-11" loading={pending}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form id="organisation-status-form" className="flex flex-col gap-4" onSubmit={onSubmit}>
        {pending ? (
          <p role="status" aria-live="polite" className="text-sm text-ink-muted">
            {closing ? "Closing the organisation. Please wait." : "Re-opening the organisation. Please wait."}
          </p>
        ) : null}
        {error?.message ? <p role="alert" className="text-sm text-danger">{error.message}</p> : null}
        {error?.consequence ? <p className="text-sm text-ink-muted">{error.consequence}</p> : null}
        <Input
          label="Reason"
          value={form.reason}
          onChange={onChange}
          className="h-11"
          disabled={pending}
          maxLength={500}
          hint="Required. Recorded in the audit trail with your name."
        />
      </form>
    </Dialog>
  );
}
